import type { KeyboardEvent, ReactNode } from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

/**
 * ResponsiveTable — one data source, two presentations.
 *
 * At or above the mobile breakpoint (768px) it renders the standard column
 * `Table`. Below it, each row becomes a stacked card: `primary` columns form the
 * card's header line, `secondary` columns are labelled key/value rows in the
 * body. This replaces the previous "wide table you scroll sideways on a phone"
 * behaviour (UIP-06 / NFR-06) without changing the data, ordering, pagination,
 * or row-click behaviour — those stay owned by the page (e.g. via
 * `QueryStateBoundary`).
 *
 * Presentation only: no querying, no loading/empty/error branches here.
 */

export type ColumnPriority = 'primary' | 'secondary';

export interface ResponsiveColumn<Row> {
  /** Stable key for the column (used for React keys and test hooks). */
  key: string;
  /** Column header (desktop) / field label (mobile card body). */
  header: string;
  /** Essential columns stay in the card header; secondary go in the card body. */
  priority: ColumnPriority;
  /** Cell renderer for a given row. */
  cell: (row: Row) => ReactNode;
  /** Optional class for the desktop `<th>`/`<td>` (e.g. width). */
  className?: string;
}

interface ResponsiveTableProps<Row> {
  columns: ResponsiveColumn<Row>[];
  rows: Row[];
  getRowKey: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  /** Accessible label for the whole row/card when `onRowClick` is set. */
  rowLabel?: (row: Row) => string;
  caption?: string;
}

export function ResponsiveTable<Row>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  rowLabel,
  caption,
}: ResponsiveTableProps<Row>) {
  const isMobile = useIsMobile();

  const interactive = typeof onRowClick === 'function';

  const handleKeyDown = (row: Row) => (e: KeyboardEvent) => {
    if (!interactive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRowClick?.(row);
    }
  };

  // ── Mobile: stacked card list ──────────────────────────────────────────────
  if (isMobile) {
    const primary = columns.filter((c) => c.priority === 'primary');
    const secondary = columns.filter((c) => c.priority === 'secondary');

    return (
      <div
        className="flex flex-col gap-2"
        data-slot="responsive-table"
        data-mode="cards"
        role="list"
      >
        {caption && <span className="sr-only">{caption}</span>}
        {rows.map((row) => {
          const key = getRowKey(row);
          return (
            <div
              key={key}
              role={interactive ? 'button' : 'listitem'}
              {...(interactive
                ? {
                    tabIndex: 0,
                    'aria-label': rowLabel?.(row),
                    onClick: () => onRowClick?.(row),
                    onKeyDown: handleKeyDown(row),
                  }
                : {})}
              data-slot="responsive-card"
              className={cn(
                'rounded-xl border border-[var(--border-default)] bg-[var(--bg-surface)] p-4',
                interactive &&
                  'cursor-pointer transition-colors hover:bg-[var(--bg-surface-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--text-accent)] focus-visible:ring-offset-2',
              )}
            >
              {/* Primary columns — card header line */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {primary.map((col) => (
                  <div key={col.key} data-slot="card-primary" data-col={col.key}>
                    {col.cell(row)}
                  </div>
                ))}
              </div>

              {/* Secondary columns — labelled key/value rows */}
              {secondary.length > 0 && (
                <dl className="mt-3 flex flex-col gap-1.5">
                  {secondary.map((col) => (
                    <div
                      key={col.key}
                      className="flex items-baseline justify-between gap-3"
                      data-slot="card-secondary"
                      data-col={col.key}
                    >
                      <dt className="text-xs text-[var(--text-tertiary)]">{col.header}</dt>
                      <dd className="text-sm text-[var(--text-secondary)] text-right">
                        {col.cell(row)}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ── Desktop: standard column table ──────────────────────────────────────────
  return (
    <div
      className="rounded-md border overflow-hidden"
      data-slot="responsive-table"
      data-mode="table"
    >
      <Table>
        {caption && <caption className="sr-only">{caption}</caption>}
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHead key={col.key} className={col.className}>
                {col.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={getRowKey(row)}
              {...(interactive
                ? {
                    // Keep the implicit role="row" — overriding it to "button"
                    // would strip table semantics from screen readers. A row can
                    // still be focusable and keyboard-activatable.
                    tabIndex: 0,
                    'aria-label': rowLabel?.(row),
                    onClick: () => onRowClick?.(row),
                    onKeyDown: handleKeyDown(row),
                    className:
                      'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--text-accent)] focus-visible:ring-inset',
                  }
                : {})}
            >
              {columns.map((col) => (
                <TableCell key={col.key} className={col.className}>
                  {col.cell(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
